import { browser } from '$app/environment';
import { redirect } from '@sveltejs/kit';
import { NAMESPACE } from '.';
import type { SpineAuthAction } from './types';
import { PROD_HOST, TEST_HOST } from '$lib/services/legacy/standaloneRegistration/constants';
import { isTest } from '$lib/services/environment';

const GAME_OVER_URL_REGEXP = new RegExp('^/([^/]+)/game-over/?');
const MAINTENANCE_URL_REGEXP = new RegExp('^/([^/]+)/maintenance/?');

export const makeSpineAuthURL = (
    url: URL,
    action: SpineAuthAction,
    options?: { redirectUrl?: string }
) => {
    const newURL = new URL(`${toMarketURL(url, true)}/${NAMESPACE}/${action}`);
    const getOriginPath = () => {
        let originPath = url.searchParams.get('originPath');
        if (!originPath) {
            if (isGameOverURL(url)) {
                originPath = chopURLPathname(url, 1).pathname;
            } else {
                originPath = url.pathname;
            }
        }
        return originPath;
    };
    const getOriginSearch = () => {
        let originSearch = url.searchParams.get('originSearch');
        if (!originSearch && Array.from(url.searchParams).length > 0) {
            originSearch = '';
            for (const [key, value] of url.searchParams) {
                if (key == 'originPath') {
                    continue;
                }
                originSearch += `${key}=${value}`;
                originSearch += '&';
            }
            originSearch = originSearch.slice(0, -1); // remove trailing '&' char
        }
        return originSearch;
    };

    newURL.searchParams.set('originPath', options?.redirectUrl || getOriginPath());
    const originSearch = getOriginSearch();
    if (originSearch) {
        newURL.searchParams.set('originSearch', originSearch);
    }
    return newURL;
};

export const toURL = (location: Location) => new URL(location.href);

/**
 * Examples:
 * 1) with removeSearchParams=false
 * http://localhost:5173/en/a/b/c/d?a=b&c=d becomes http://localhost:5173/en?a=b&c=d
 *
 * 2) with removeSearchParams=true
 * http://localhost:5173/en/a/b/c/d?a=b&c=d becomes http://localhost:5173/en
 *
 * @param url The URL to base the new URL on
 * @param removeSearchParams If the searchParams should be removed
 * @returns A new modified URL
 */
export const toMarketURL = (url: URL, removeSearchParams = false) => {
    let newURL = new URL(chopURLPathname(url, 1).href);
    if (removeSearchParams) {
        newURL = toURLWithoutSearchParams(newURL);
    }
    return newURL;
};

export const gotoGameOverURL = (originURL: URL) => {
    if (isGameOverURL(originURL)) {
        return;
    }

    originURL.host = isTest(originURL.origin) ? TEST_HOST : PROD_HOST;
    originURL.hostname = isTest(originURL.origin) ? TEST_HOST : PROD_HOST;
    originURL.protocol = 'https';
    originURL.port = '443';

    const gameOverURL = new URL(`${toMarketURL(originURL).href}/404`);

    if (browser) {
        window.location.href = gameOverURL.toString();
    } else {
        throw redirect(302, gameOverURL.toString());
    }
};

export const isGameOverURL = (url?: URL) =>
    !url ? false : GAME_OVER_URL_REGEXP.test(url.pathname);
export const isMaintenanceURL = (url?: URL) =>
    !url ? false : MAINTENANCE_URL_REGEXP.test(url.pathname);

export const toURLWithoutSearchParams = (url: URL, ...keepParams: string[]) => {
    const newURL = new URL(url);
    const searchParams = [...newURL.searchParams];
    for (const [key] of searchParams) {
        if (!keepParams || (keepParams && !keepParams.includes(key))) {
            newURL.searchParams.delete(key);
        }
    }
    return newURL;
};

export const toURLWithOrigins = (url: URL) => {
    const newURL = new URL(`${url.origin}${url.searchParams.get('originPath') ?? ''}`);
    const originSearch = url.searchParams.get('originSearch');
    if (originSearch) {
        newURL.search = originSearch;
    }
    return newURL;
};
/* eslint-disable  @typescript-eslint/no-explicit-any */
export const endOfRegistrationLogin = (url: string, auth: any) => {
    try {
        const redirectUrl = new URL(url);
        const originPath = redirectUrl.searchParams.get('originPath');

        auth.logIn({ redirectUrl: originPath });
    } catch (e) {
        console.error(e);
    }
};

export const goToRegisterPage = (originURL: URL) => {
    const registerPageURL = new URL(`${toMarketURL(originURL, true)}/register`);

    if (!originURL.searchParams.get('originPath')?.includes('register')) {
        registerPageURL.searchParams.set(
            'originPath',
            `${originURL.searchParams.get('originPath')}`
        );
    }

    const originSearch = originURL.searchParams.get('originSearch');
    if (originSearch) {
        originURL.search = originSearch;
    }
    if (browser) {
        window.location.href = registerPageURL.toString();
    } else {
        throw redirect(302, registerPageURL.toString());
    }
};

const chopURLPathname = (url: URL, index: number) => {
    if (index < 0) {
        index = 0;
    }
    const pathParts = url.pathname.split('/');
    if (index > pathParts.length) {
        index = pathParts.length - 1;
    }
    const newURL = new URL(`${url.origin}/${pathParts.at(index)}`);
    for (const [key, value] of url.searchParams) {
        newURL.searchParams.set(key, value);
    }
    return newURL;
};
